import { appFeedbackTourStepItems } from 'components/appFeedback/appFeedback.steps';
import { dashboardTourStepItems } from 'components/dashboard/guideTour/DashboardGuideTour.steps';
import type { IntlKey } from 'locales';
import type { IntlShape } from 'react-intl';
import type { PopperPlacement, StepOptions, StepOptionsButton, TourOptions } from 'shepherd.js';

export interface TourStepItem {
  id: string;
  on?: PopperPlacement;
}

// Main configuration of Shepherd

export const tourOptions: TourOptions = {
  defaultStepOptions: {
    cancelIcon: {
      enabled: true,
    },
  },
  useModalOverlay: true,
};

/**
 * Steps definition, depending on the location of the user
 * Be careful to not rename already persisted completed steps
 */
const tourStepItems: TourStepItem[] = [...dashboardTourStepItems, ...appFeedbackTourStepItems];

/**
 * Add the "Previous" and "Next" buttons, depending on the current position.
 */
function getButtons(
  formatMessage: IntlShape['formatMessage'],
  index: number,
  stepsCount: number,
): readonly StepOptionsButton[] {
  const buttons = [];
  if (index > 0) {
    buttons.push({
      action() {
        this.back();
      },
      text: formatMessage({ id: 'tour.button.previous' }),
      secondary: true,
    });
  }
  if (index === stepsCount - 1) {
    buttons.push({
      action() {
        this.hide();
      },
      text: formatMessage({ id: 'tour.button.close' }),
    });
  } else {
    buttons.push({
      action() {
        this.next();
      },
      text: formatMessage({ id: 'tour.button.next' }),
    });
  }
  return buttons;
}

/**
 * Get the steps depending on the location of the user.
 * In our example, we will consider the steps for the page /foo/* and for other route.
 */
export function getTourSteps(
  stepIds: string[],
  { formatMessage }: IntlShape,
  onStepCompleted: (stepId: string) => void,
): StepOptions[] {
  const existingSteps = tourStepItems.filter(({ id }) => stepIds.includes(id));
  return existingSteps.map((item, index: number): StepOptions => ({
    id: item.id,
    attachTo: {
      element: `[data-tour="${item.id}"]`,
      on: item.on || 'bottom',
    },
    buttons: getButtons(formatMessage, index, existingSteps.length),
    title: `<span style="max-width: 350px; word-break: break-all; word-wrap: break-word">${formatMessage({
      id: `tour.step.${item.id}.title` as IntlKey,
    })}</span>`,
    text: formatMessage({ id: `tour.step.${item.id}.text` as IntlKey }),
    // Add the progression indicator:
    when: {
      show() {
        const currentStepElement = this.el;
        if (currentStepElement) {
          const footer = currentStepElement.querySelector('.shepherd-footer');
          if (footer) {
            const progress = document.createElement('span');
            progress.classList.add('shepherd-progress');
            progress.innerHTML = formatMessage(
              { id: 'tour.progression' },
              { step: index + 1, total: existingSteps.length },
            );
            footer.insertBefore(progress, currentStepElement.querySelector('.shepherd-button'));
          }
        }
      },
      hide() {
        onStepCompleted(item.id);
      },
    },
  }));
}
